import * as React from "react";
import { alpha } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import FilterListIcon from "@mui/icons-material/FilterList";
import { visuallyHidden } from "@mui/utils";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import DialogQualityCheck from "../dialog/DialogQualityCheck";
import Snackbar from "@mui/material/Snackbar";
import {
  DatePicker,
  DateTimePicker,
  DateTimePickerTabs,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { it } from "date-fns/locale";

import {
  Alert,
  Autocomplete,
  Button,
  capitalize,
  FormControl,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "../../providers/AuthProvider";
import noData from "../../assets/svg/undraw_no-data.svg";
import { collection, onSnapshot } from "@firebase/firestore";
import { db } from "../../services/firebase";
import { nomeCognomeRegex, truncateWord } from "../../services/shared";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
export interface Lead {
  uid: string;
  cellulare: string;
  status: string;
  createdAt: Date;
  nominativo: string;
  shortLink: string;
  uidSender: string;
}
const STATI = ["quality check", "attesa privacy", "inviato", "neutralizzato"];
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string | Date },
  b: { [key in Key]: number | string | Date },
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Lead | "actions";
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "nominativo",
    numeric: false,
    disablePadding: false,
    label: "Nominativo",
  },
  {
    id: "status",
    numeric: false,
    disablePadding: false,
    label: "Stato consenso",
  },
  {
    id: "cellulare",
    numeric: false,
    disablePadding: false,
    label: "Cellulare",
  },
  {
    id: "createdAt",
    numeric: false,
    disablePadding: false,
    label: "Data creazione",
  },
  {
    id: "uidSender",
    numeric: false,
    disablePadding: false,
    label: "Operatore",
  },
  {
    id: "actions",
    numeric: false,
    disablePadding: false,
    label: "Azioni",
  },
];

interface EnhancedTableProps {
  numSelected: number;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Lead,
  ) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;
  const createSortHandler =
    (property: keyof Lead) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            style={{ fontWeight: "600" }}
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id as keyof Lead)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface EnhancedTableToolbarProps {
  numSelected: number;
}

interface TableProps {
  data: Lead[];
}

function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const { numSelected } = props;
  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
        },
        numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity,
            ),
        },
      ]}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Typography
          sx={{ flex: "1 1 100%" }}
          variant="h6"
          id="tableTitle"
          component="div"
        ></Typography>
      )}
      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton>
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </Toolbar>
  );
}

interface TableRowItemProps {
  uid: string;
  nominativo: string;
  status: string;
  cellulare: string;
  createdAtMs: number;
  displayName: string;
  isItemSelected: boolean;
  labelId: string;
  onAction: (action: "switchState" | "copyText" | "", uid: string) => void;
}

const TableRowItem = React.memo(({
  uid, nominativo, status, cellulare, createdAtMs, displayName,
  isItemSelected, labelId, onAction,
}: TableRowItemProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (action: "switchState" | "copyText" | "") => {
    setAnchorEl(null);
    onAction(action, uid);
  };

  return (
    <TableRow hover tabIndex={-1} selected={isItemSelected} sx={{ cursor: "pointer" }}>
      <TableCell component="th" align="left" id={labelId} scope="row" padding="normal">
        {status === "attesa privacy" ? "--" : truncateWord(nominativo, 25, false)}
      </TableCell>
      <TableCell padding="normal" align="left">
        <span className={`labelStatus ${status === "inviato" || status === "confermato" ? "confirm" : ""}`}>
          {status}
        </span>
      </TableCell>
      <TableCell padding="normal" align="left">
        {status === "attesa privacy" ? "--" : truncateWord(cellulare, 12, false)}
      </TableCell>
      <TableCell padding="normal" align="left">
        {format(new Date(createdAtMs), "dd/MM/yyyy HH:mm")}
      </TableCell>
      <TableCell padding="normal" align="left">
        {displayName}
      </TableCell>
      <TableCell padding="normal" align="left">
        <Button onClick={handleOpenMenu}>Azioni</Button>
        <Menu
          id={`menu-${uid}`}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem onClick={() => handleMenuClose("switchState")}>Cambia stato</MenuItem>
          <MenuItem onClick={() => handleMenuClose("copyText")}>Copia testo conferma</MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
});

export default function EnhancedTable({ data }: TableProps) {
  const { customClaims } = useAuth();
  const [originalRows, setOriginalRows] = useState(data);
  const [snackbarData, setSnackbarData] = React.useState<{
    severity: "warning" | "success";
    message: string;
  }>({ severity: "warning", message: "Non puoi modificare questo lead" });
  const [snackState, setSnackState] = React.useState<boolean>(false);
  const [filters, setFilters] = useState<{
    cellulare: string;
    stato:
      | "quality check"
      | "attesa privacy"
      | "inviato"
      | "tutti"
      | "neutralizzato";
    operatore: string;
    dateRange: { start: Date | null; end: Date | null };
  }>({
    cellulare: "",
    stato: "tutti",
    operatore: "",
    dateRange: { start: null, end: null },
  });
  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Lead>("createdAt");
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [selectedLead, setSelectedLead] = React.useState<any>({});
  const [dialogState, setDialogState] = React.useState<boolean>(false);
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(true);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [rows, setRows] = useState(data);
  const [canHandle, setCanHandle] = useState<boolean>(false);
  const [userMap, setUserMap] = useState<any>();
  // refs for stable handleAction callback — always reflect current values without deps
  const dataRef = React.useRef(data);
  dataRef.current = data;
  const canHandleRef = React.useRef(canHandle);
  canHandleRef.current = canHandle;
  const handleAction = React.useCallback((action: "switchState" | "copyText" | "", uid: string) => {
    const lead = dataRef.current.find((d) => d.uid === uid);
    if (!lead) return;
    if (canHandleRef.current && lead.status === "quality check" && action === "switchState") {
      setSelectedLead(null);
      setDialogState(false);
      setTimeout(() => {
        setSelectedLead(lead);
        setDialogState(true);
      }, 0);
    } else if (canHandleRef.current && lead.status === "attesa privacy" && action === "copyText") {
      const txt_v2 = `
       La preghiamo di verificare la correttezza dei suoi dati. Cliccando sul link: https://preq.affida.credit/s/${lead.shortLink}
      `;
      navigator.clipboard.writeText(txt_v2)
        .then(() => {
          setSnackbarData({ severity: "success", message: "Testo copiato" });
          setSnackState(true);
        })
        .catch((err) => console.error("Errore nella copia:", err));
    } else {
      setSnackbarData({ severity: "warning", message: "Azione non consentita" });
      setSnackState(true);
    }
  }, []); // stable — uses refs for live data and canHandle
  const [operatorOptions, setOperatorOptions] = useState<
    { uid: string; displayName: string }[]
  >([]);
  const phoneRegex = /^(?:(?:\+39|0039)?\s?)?(3\d{2})\s?\d{6,7}$/;
  const phoneFormSchema = z.object({
    cellulare: z
      .string()
      .min(8, "Cellulare non valido")
      .regex(phoneRegex, "Numero di cellulare italiano non valido")
      .or(z.literal("")), // accetta anche stringa vuota
    stato: z
      .enum([
        "quality check",
        "attesa privacy",
        "inviato",
        "tutti",
        "neutralizzato",
      ])
      .optional(),
    dateRange: z
      .object({
        start: z
          .date({ required_error: "Data inizio obbligatoria" })
          .nullable()
          .optional(),
        end: z
          .date({ required_error: "Data fine obbligatoria" })
          .nullable()
          .optional(),
      })
      .refine(
        (range) => !range.start || !range.end || range.end >= range.start,
        {
          message: "La data di fine deve essere successiva a quella di inizio",
          path: ["end"],
        },
      )
      .optional(),
    operatore: z.string().optional(),
  });
  type FormData = z.infer<typeof phoneFormSchema>;
  const form = useForm<FormData>({
    resolver: zodResolver(phoneFormSchema),
    defaultValues: {
      cellulare: "",
      stato: "tutti",
      dateRange: {
        start: null,
        end: null,
      },
      operatore: "",
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = form;

  const onSubmit = handleSubmit((formValues, evt) => {
    evt?.preventDefault();
    onFilter(formValues as any);
  });

  const normalizePhone = (v: string) => v.replace(/\D/g, "");

  const onFilter = (fVal: {
    cellulare: string;
    operatore: string;
    stato:
      | "quality check"
      | "attesa privacy"
      | "inviato"
      | "tutti"
      | "neutralizzato";
    dateRange: { start: Date | null; end: Date | null };
  }) => {
    // console.log({ fVal });
    setFilters(fVal);

    let filtered = originalRows;

    // cellulare
    if (fVal.cellulare) {
      const q = normalizePhone(fVal.cellulare);
      if (q) {
        filtered = filtered.filter((r) =>
          normalizePhone(r.cellulare).includes(q),
        );
      }
    }

    // operatore

    // stato
    if (fVal.stato && fVal.stato !== "tutti") {
      filtered = filtered.filter((r) => r.status === fVal.stato);
      // console.log(filtered);
    }

    // date range
    if (fVal.dateRange?.start && fVal.dateRange?.end) {
      const { start, end } = fVal.dateRange;
      filtered = filtered.filter((r) => {
        const createdAt = new Date(r.createdAt);
        return createdAt >= start && createdAt <= end;
      });
    }

    // operatore
    if (fVal.operatore) {
      filtered = filtered.filter((r) => r.uidSender === fVal.operatore);
    }

    setRows(filtered);
    setPage(0);
  };

  useEffect(() => {
    setRows(data);
    setOriginalRows(data);
    // quando data cambia, riapplico i filtri correnti
    onFilter(filters);
    const canHandle = (customClaims?.grants ?? []).some((role: string) =>
      ["quality_op", "admin"].includes(role),
    );
    setCanHandle(canHandle);
  }, [data]);

  function getUidByDisplayName(
    name: string,
    map: Record<string, string>,
  ): string | undefined {
    return Object.keys(map).find((uid) => map[uid] === name);
  }

  useEffect(() => {
    // carico tutti gli utenti una volta sola
    const q = collection(db, "users");
    const unsubscribe = onSnapshot(q, (snap) => {
      const map: Record<string, string> = {};
      const correctMap: Record<string, string> = {};
      snap.forEach((doc) => {
        const data = doc.data();
        map[doc.id] = capitalize(data.displayName);
      });
      setUserMap(map);
      const operatorOptions = Object.entries(map).map(([uid, displayName]) => ({
        uid,
        displayName,
      }));
      setOperatorOptions(operatorOptions);
    });
    return unsubscribe;
  }, []);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Lead,
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.uid);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangeDense = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const visibleRows = React.useMemo(
    () =>
      [...rows]
        .sort(getComparator(order, orderBy))
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, rows],
  );

  return (
    <Box sx={{ width: "100%" }}>
      {dialogState && <DialogQualityCheck value={selectedLead} />}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackState}
        onClose={() => setSnackState(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setSnackState(false)}
          severity={snackbarData.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbarData.message}
        </Alert>
      </Snackbar>
      <div>
        <form
          onSubmit={(event) => onSubmit(event)}
          style={{
            width: "fit-content",
            minWidth: "275px",
            marginBottom: "1em",
            marginTop: "1em",
            alignItems: "baseline",
          }}
          className="flex-col gap-2"
        >
          <div className={"flex-row gap-2 w100"}>
            <Controller
              name="cellulare"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Filtra per cellulare"
                  placeholder="Inserisci cellulare"
                  value={field.value ?? ""}
                  error={!!errors.cellulare}
                  helperText={errors.cellulare?.message}
                />
              )}
            />
            <Controller
              name="stato"
              control={control}
              render={({ field }) => (
                <FormControl size="small" fullWidth>
                  <InputLabel id="stato-label">Stato</InputLabel>
                  <Select
                    {...field}
                    labelId="stato-label"
                    label="Stato"
                    value={field.value ?? ""}
                  >
                    <MenuItem value="tutti">Tutti</MenuItem>
                    {STATI.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </div>
          <div className={"flex-row gap-2 w100"}>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={it}
            >
              {/* START */}
              <Controller
                name="dateRange.start"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Data inizio"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: !!errors?.dateRange?.start,
                        helperText: errors?.dateRange?.start?.message,
                      },
                    }}
                  />
                )}
              />
              {/* END */}
              <Controller
                name="dateRange.end"
                control={control}
                render={({ field }) => (
                  <DatePicker
                    label="Data fine"
                    value={field.value ?? null}
                    onChange={field.onChange}
                    slotProps={{
                      textField: {
                        size: "small",
                        fullWidth: true,
                        error: !!errors?.dateRange?.end,
                        helperText: errors?.dateRange?.end?.message,
                      },
                    }}
                  />
                )}
              />

              {errors.root && (
                <p style={{ color: "red" }}>{errors.root.message}</p>
              )}
            </LocalizationProvider>
            <Controller
              name="operatore"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={operatorOptions}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.displayName
                  }
                  value={
                    operatorOptions?.find((op) => op.uid === field.value) ||
                    null
                  }
                  onChange={(_, newValue) => {
                    if (!newValue) {
                      field.onChange("");
                    } else if (typeof newValue === "string") {
                      field.onChange("");
                    } else {
                      field.onChange(newValue.uid);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Filtra per operatore"
                      size="small"
                      error={!!errors.operatore}
                      helperText={errors.operatore?.message}
                      sx={{ width: 225 }}
                    />
                  )}
                />
              )}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              style={{ maxHeight: "fit-content" }}
            >
              Cerca
            </Button>
            <Button
              onClick={() => {
                setRows(originalRows);
                form.reset();
              }}
              style={{ maxHeight: "fit-content" }}
            >
              Reset
            </Button>
          </div>
        </form>
      </div>
      <Paper sx={{ width: "100%", mb: 2 }}>
        {/*<EnhancedTableToolbar numSelected={selected.length} />*/}
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={dense ? "small" : "medium"}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {visibleRows.map((row, index) => (
                <TableRowItem
                  key={row.uid}
                  uid={row.uid}
                  nominativo={row.nominativo}
                  status={row.status}
                  cellulare={row.cellulare}
                  createdAtMs={row.createdAt instanceof Date ? row.createdAt.getTime() : new Date(row.createdAt).getTime()}
                  displayName={userMap?.[row.uidSender] ?? "—"}
                  isItemSelected={selected.includes(row.uid)}
                  labelId={`enhanced-table-checkbox-${index}`}
                  onAction={handleAction}
                />
              ))}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: (dense ? 33 : 53) * emptyRows,
                  }}
                >
                  <TableCell colSpan={6}>Nessun dato</TableCell>
                </TableRow>
              )}
              {visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <div
                      style={{ alignItems: "center" }}
                      className={"flex-col"}
                    >
                      <img
                        style={{ width: "125px", paddingBottom: ".5em" }}
                        src={noData}
                        alt="delle cartelline vuote"
                      />
                      <span style={{ fontWeight: "300", fontSize: "14px" }}>
                        Nessun dato
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      {/*<FormControlLabel*/}
      {/*  control={<Switch checked={dense} onChange={handleChangeDense} />}*/}
      {/*  label="Dense padding"*/}
      {/*/>*/}
    </Box>
  );
}
